import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { NJIcon, NJInlineMessage, NJTag } from '@engie-group/fluid-design-system-react';
import type { ColDef } from 'ag-grid-community';
import PrototypeStudioLayout from '@shared/PrototypeStudioLayout';
import { AgGridReact } from 'ag-grid-react';
import { SIZE2MIX_AG_GRID_LOCALE, SIZE2MIX_AG_GRID_PAGE_SIZES, Size2MixPageHeader, Size2MixStatusTag, NetworkShell, TEST_PUC_NETWORK, useSize2MixRefNavigation } from '@shared/size2mix-shell';
import OptimisationActionsCell, { type ActionsContext } from './ActionsCell';
import CreateOptimisationMenu from './CreateOptimisationMenu';
import { useOptimisationActions, useOptimisations, useResetOptimisations } from './hooks';
import { NETWORK_ACCESS, VIEWERS, computePermissions, isViewerId } from './permissions';
import type { OptimisationSummary, ViewerId } from './types';
import ViewerSwitcher from './ViewerSwitcher';

export const meta = {
    title: 'Référence — Réseau > Optimisations',
    description:
        "Page de référence « Liste des optimisations » (PUC EDM) : bouton de création avec menu des types, AG Grid filtrable (50 lignes/page) avec badge statut et tag type, copie et suppression confirmée. Le nom ouvre le détail. Sélecteur « Voir en tant que » ou ?as=owner|write|read.",
    tags: ['optimization', 'list', 'reference'],
    author: 'dv5886',
    status: 'review' as const,
    category: 'optimisations/list',
    createdAt: '2026-10-10',
};

interface GridContext extends ActionsContext {
    onOpen: () => void;
}

export default function RefOptimisationList() {
    const nav = useSize2MixRefNavigation();
    const [searchParams, setSearchParams] = useSearchParams();
    const asParam = searchParams.get('as');
    const viewerId: ViewerId = isViewerId(asParam) ? asParam : 'owner';
    const perms = computePermissions(NETWORK_ACCESS, VIEWERS[viewerId]);

    const { data = [] } = useOptimisations();
    const { create, remove } = useOptimisationActions(viewerId);
    const reset = useResetOptimisations();
    const error = create.error ?? remove.error;

    const setViewer = (id: ViewerId) => {
        const next = new URLSearchParams(searchParams);
        next.set('as', id);
        setSearchParams(next, { replace: true });
        create.reset();
        remove.reset();
    };

    const context: GridContext = {
        canEdit: perms.canEdit,
        onClone: (row) => create.mutate({ copyOf: row.eid }),
        onDelete: (row) => remove.mutate(row.eid),
        onOpen: () => nav.goTo('optimisationDetail'),
    };

    const columns = useMemo<ColDef<OptimisationSummary>[]>(() => [
        {
            headerName: 'Statut',
            field: 'status',
            cellRenderer: (p: { value: string; data?: OptimisationSummary }) => (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <Size2MixStatusTag label={p.value} />
                    {p.data?.source === 'Optimisations' && <NJIcon name="report" size="md" style={{ color: 'var(--nj-core-color-red-600)' }} />}
                </span>
            ),
        },
        { headerName: 'Type', field: 'type', cellRenderer: (p: { value: string }) => <NJTag label={p.value} variant="ultramarine" size="xs" /> },
        {
            headerName: 'Nom',
            field: 'name',
            cellRenderer: (p: { data?: OptimisationSummary; context: GridContext }) => (
                <a href="#" className="nj-link nj-link--contextual" style={{ fontSize: 14, color: 'var(--nj-semantic-color-text-neutral-primary-default)', textDecoration: 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} onClick={(event) => { event.preventDefault(); p.context.onOpen(); }}>
                    {p.data?.name}
                </a>
            ),
        },
        { headerName: 'Nombre de pas', field: 'steps' },
        { headerName: 'Créée par', field: 'createdBy' },
        { headerName: 'Date', field: 'date', sort: 'desc' },
        {
            headerName: 'Actions',
            sortable: false,
            filter: false,
            cellRenderer: (p: { data?: OptimisationSummary; context: GridContext }) => <OptimisationActionsCell data={p.data} context={p.context} />,
        },
    ], []);

    return (
        <PrototypeStudioLayout title={meta.title}>
            <NetworkShell network={TEST_PUC_NETWORK} activePage="optimisations" onNavigate={nav.onNetworkNavigate} onPrimaryNavigate={nav.onPrimaryNavigate}>
                <div style={{ padding: 20 }}>
                    <Size2MixPageHeader title={`Liste des optimisations (${data.length})`} actions={<CreateOptimisationMenu onCreate={(type) => create.mutate({ type })} />} />
                    {error && (
                        <div style={{ marginTop: 16 }}>
                            <NJInlineMessage variant="error" title={error.message} />
                        </div>
                    )}
                    <div className="ag-theme-quartz ref-optimisation-grid" style={{ marginTop: 16 }}>
                        <style>{`.ref-optimisation-grid .ag-header-cell-text{font-weight:700;color:var(--nj-semantic-color-text-neutral-secondary-default)}.ref-optimisation-grid .ag-cell{color:var(--nj-semantic-color-text-neutral-primary-default)}.ref-optimisation-grid .ag-header-cell::after{display:none}`}</style>
                        <AgGridReact<OptimisationSummary>
                            rowData={data}
                            columnDefs={columns}
                            context={context}
                            defaultColDef={{ width: 150, sortable: true, filter: true, resizable: true, suppressMovable: true }}
                            domLayout="autoHeight"
                            localeText={SIZE2MIX_AG_GRID_LOCALE}
                            pagination
                            paginationPageSize={50}
                            paginationPageSizeSelector={SIZE2MIX_AG_GRID_PAGE_SIZES}
                        />
                    </div>
                </div>
            </NetworkShell>
            <ViewerSwitcher viewer={viewerId} perms={perms} onChange={setViewer} onReset={() => reset.mutate()} />
        </PrototypeStudioLayout>
    );
}

